import { PortalScheduleSlot, PortalSession, createPortalClient } from './portalClient';

export { PHOTO_TYPES, MAX_PHOTO_BYTES, SessionExpiredError } from './portalClient';
export type { PortalScheduleSlot } from './portalClient';

// En producción el portal se activa con REACT_APP_STUDENT_PORTAL=true cuando Siloe tenga el endpoint desplegado.
export const STUDENT_PORTAL_ENABLED =
  process.env.REACT_APP_STUDENT_PORTAL === 'true' || process.env.NODE_ENV !== 'production';

const ROLE_STUDENT = 3;

const client = createPortalClient({
  storageKey: 'cecap_student_session',
  role: ROLE_STUDENT,
  wrongRoleMessage: 'Esta cuenta no es de estudiante. Usa el portal que corresponde a tu cuenta.',
  photoPath: '/students/portal/me/photo',
});

export type StudentSession = PortalSession;

export type SubjectStatus = 'PENDING' | 'PASSED' | 'FAILED';
export type ModuleStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface PortalGradeComponent {
  name: string;
  weight: number;
  value: number | null;
}

export interface PortalSubject {
  subject: string;
  grade: number | null;
  components?: PortalGradeComponent[];
  partial?: number | null;
  status: SubjectStatus;
  enrolled?: boolean;
  teacher: string | null;
  classroom: string | null;
  modality: string | null;
  course_label: string | null;
}

export interface PortalModule {
  id: number;
  name: string;
  order: number;
  status: ModuleStatus;
  result: 'PASSED' | 'FAILED' | null;
  subjects: PortalSubject[];
}

export interface PortalScheduleItem {
  module: string;
  subject: string;
  schedule_info: string | null;
  schedule_slots?: PortalScheduleSlot[];
  teacher: string | null;
  classroom: string | null;
  modality: string | null;
  course_label: string | null;
}

export interface PortalCareer {
  enrollment_id: number;
  career: { id: number; name: string; code: string; progression_mode?: 'SEQUENTIAL' | 'FLEXIBLE' };
  status: string;
  enrollment_date: string;
  current_module: string | null;
  passing_grade: number;
  max_grade: number;
  modules: PortalModule[];
  schedule: PortalScheduleItem[];
}

export interface PortalPayment {
  id: number;
  date: string;
  amount: number;
  concept: string | null;
  description: string;
  payment_method: string;
  receipt_number: string | null;
}

export interface StudentPortalData {
  student: {
    id: number;
    full_name: string;
    username: string | null;
    student_code: string | null;
    document_number: string;
    email: string | null;
    photo?: string | null;
  };
  school: { name: string; logo: string | null } | null;
  careers: PortalCareer[];
  payments: PortalPayment[];
}

export const getStudentSession = client.getSession;
export const logoutStudent = client.logout;
export const studentLogin = client.login;
export const uploadStudentPhoto = client.uploadPhoto;
export const changeStudentPassword = client.changePassword;
export const forceChangeStudentPassword = client.forceChangePassword;

export function fetchStudentPortal(): Promise<StudentPortalData> {
  return client.request<StudentPortalData>('/students/portal/me', 'No se pudieron cargar tus datos. Intenta más tarde.');
}

export type OfferedSubjectStatus = 'AVAILABLE' | 'IN_PROGRESS' | 'PASSED' | 'BLOCKED' | 'NOT_OFFERED';

export interface SubjectOfferingOption {
  id: number;
  schedule_info: string | null;
  modality: string | null;
  teacher: string | null;
  classroom: string | null;
  period: string | null;
  capacity: number | null;
  enrolled_count: number;
  has_capacity: boolean;
  already_in: boolean;
}

export interface OfferedSubject {
  subject_id: number;
  subject: string;
  module: string;
  module_order: number;
  status: OfferedSubjectStatus;
  reason: string;
  prerequisite: string | null;
  can_enroll: boolean;
  offerings: SubjectOfferingOption[];
}

export interface CareerOfferings {
  enrollment_id: number;
  career: string;
  subjects: OfferedSubject[];
  available_count: number;
}

export function fetchCareerOfferings(enrollmentId: number): Promise<CareerOfferings> {
  return client.request<CareerOfferings>(
    `/students/portal/careers/${enrollmentId}/offerings`,
    'No se pudieron cargar las materias ofertadas.',
  );
}

export async function enrollInOffering(enrollmentId: number, offeringId: number): Promise<string> {
  const response = await client.authedFetch(`/students/portal/careers/${enrollmentId}/enroll`, {
    method: 'POST',
    body: JSON.stringify({ offering_id: offeringId }),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(body?.errors?.[0]?.description || body?.message || 'No se pudo matricular la materia.');
  }
  return body?.message || 'Matrícula realizada.';
}
